import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { AlertTriangle, CheckCircle, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/primitives/Alert";

export function EdgeFunctionsStatus() {
	const [functionsWorking, setFunctionsWorking] = useState<boolean | null>(
		null,
	);
	const [isDismissed, setIsDismissed] = useState(false);
	const [isChecking, setIsChecking] = useState(true);

	useEffect(() => {
		checkEdgeFunctions();
	}, []);

	const checkEdgeFunctions = async () => {
		try {
			setIsChecking(true);

			// Use a simple OPTIONS request to check if function endpoints exist
			// This avoids authentication issues and 400 errors
			const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

			if (!supabaseUrl) {
				console.log("DB URL configured");
				setFunctionsWorking(false);
				return;
			}

			try {
				// Test with OPTIONS request to avoid authentication and body validation
				const response = await fetch(
					`${supabaseUrl}/functions/v1/ai-generate`,
					{
						method: "OPTIONS",
						headers: {
							apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "",
						},
					},
				);

				if (response.status === 404) {
					// Function not deployed
					setFunctionsWorking(false);
				} else if (
					response.status === 200 ||
					response.status === 401 ||
					response.status === 400
				) {
					// Function exists (200 = deployed, 401/400 = deployed but needs auth/data)
					setFunctionsWorking(true);
				} else {
					// Other status codes - assume not working
					setFunctionsWorking(false);
				}
			} catch (fetchError) {
				// Network errors or CORS issues mean functions might not be available
				console.log(
					"Edge function availability check failed (this is normal if functions aren't deployed)",
				);
				setFunctionsWorking(false);
			}
		} catch (error) {
			console.log("Edge functions check failed:", error);
			setFunctionsWorking(false);
		} finally {
			setIsChecking(false);
		}
	};

	// Don't show anything if dismissed or still checking
	if (isDismissed || isChecking) {
		return null;
	}

	// Don't show if functions are working
	if (functionsWorking) {
		return null;
	}

	return (
		<Alert className="mb-4 border-orange-200 bg-orange-50">
			<AlertTriangle className="h-4 w-4 text-orange-600" />
			<AlertDescription className="flex items-center justify-between">
				<div className="flex-1">
					<strong className="text-orange-800">AI Features Not Available</strong>
					<p className="text-sm text-orange-700 mt-1">
						Edge Functions are not deployed. AI analysis will show "not
						available" messages.
						<br />
						<span className="text-xs">
							To enable AI: Deploy functions with{" "}
							<code>supabase functions deploy ai-generate</code>
						</span>
					</p>
				</div>
				<button
					onClick={() => setIsDismissed(true)}
					className="ml-2 text-orange-600 hover:text-orange-800"
				>
					<X className="h-4 w-4" />
				</button>
			</AlertDescription>
		</Alert>
	);
}
